import React, { useState, useEffect } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * HotelConfirmation - Single-screen confirmation flow
 * Shows verified card directly, with inline edit option if needed
 */
const HotelConfirmation = ({ 
  extractedData, 
  formData, 
  onConfirm, 
  onBack 
}) => {
  // Initialize from extracted data
  const [isVerifying, setIsVerifying] = useState(true); // starts verifying immediately
  const [showEdit, setShowEdit] = useState(false);
  
  const [hotelName, setHotelName] = useState(extractedData?.hotelName || extractedData?.name || '');
  const [city, setCity] = useState(extractedData?.city || '');
  const [country, setCountry] = useState(extractedData?.country || '');
  const [mapsUrl, setMapsUrl] = useState('');

  const canStart = hotelName.trim() && country.trim();

  // Field style function matching mockup
  const fieldStyle = (value, required) => ({
    width: "100%",
    boxSizing: "border-box",
    background: "#FFFFFF",
    border: `1px solid ${required && !value.trim() ? "#DC2626" : "rgba(10,10,26,0.12)"}`,
    borderRadius: "10px",
    padding: "12px 16px",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "14px",
    color: "#0A0A1A",
    outline: "none",
    transition: "border-color 0.2s",
  });

  // Label style matching mockup
  const labelStyle = {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "11.5px",
    fontWeight: 600,
    color: "rgba(10,10,26,0.5)",
    marginBottom: "6px",
    display: "block",
  };

  // Auto-verify on mount
  useEffect(() => {
    const verify = async () => {
      if (!hotelName.trim() || !country.trim()) {
        setIsVerifying(false);
        setShowEdit(true);
        return;
      }
      try {
        const response = await fetch(`${API}/recheck-gbp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hotelName, city, country })
        });
        const data = await response.json();
        if (data.success && data.gbpFound && data.matchedName && !data.matchedName.startsWith('http')) {
          setHotelName(data.matchedName);
        }
      } catch (err) {
        console.error('GBP verification failed:', err);
      } finally {
        setIsVerifying(false);
      }
    };
    verify();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReconfirm = async () => {
    if (!canStart) return;
    setIsVerifying(true);
    try {
      const response = await fetch(`${API}/recheck-gbp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hotelName, city, country })
      });
      const data = await response.json();
      if (data.success && data.gbpFound && data.matchedName && !data.matchedName.startsWith('http')) {
        setHotelName(data.matchedName);
      }
    } catch (err) {
      console.error('GBP re-verification failed:', err);
    } finally {
      setIsVerifying(false);
      setShowEdit(false);
    }
  };

  // Handle final audit confirmation
  const handleStartAudit = () => {
    onConfirm({
      ...formData,
      confirmedHotelName: hotelName,
      confirmedCity: city,
      confirmedCountry: country,
      googleListingUrl: mapsUrl || null
    });
  };

  // Location display text
  const locationText = [city, country].filter(Boolean).join(", ") || "Location not specified";

  return (
    <div style={{ maxWidth: "480px", margin: "0 auto", padding: "40px 20px" }}>
      <div style={{
        background: "#FFFFFF",
        border: "1px solid rgba(10,10,26,0.08)",
        borderRadius: "20px",
        padding: "40px 36px",
        boxShadow: "0 4px 6px rgba(0,0,0,0.03), 0 20px 60px rgba(0,0,0,0.07)",
        position: "relative",
      }}>
        {/* Blue accent line */}
        <div style={{
          position: "absolute", top: 0, left: "24px", right: "24px", height: "2px",
          background: "linear-gradient(90deg, #2D4DE0, #4A6FE3, rgba(45,77,224,0))",
        }} />

        {/* Header */}
        <h2 style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: "22px",
          fontWeight: 800,
          color: "#0A0A1A",
          textAlign: "center",
          marginBottom: "8px",
        }}>
          {isVerifying ? "Finding your hotel on Google..." : "Is this how your hotel appears on Google?"}
        </h2>
        <p style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "13.5px",
          color: "rgba(10,10,26,0.45)",
          textAlign: "center",
          marginBottom: "32px",
          lineHeight: 1.6,
        }}>
          {isVerifying
            ? "Verifying your Google Business Profile..."
            : "This name will be used to find your hotel on Google. Make sure it matches your Google listing exactly."}
        </p>

        {isVerifying ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}>
            <svg width="32" height="32" viewBox="0 0 32 32" style={{ animation: "spin 1s linear infinite" }}>
              <circle cx="16" cy="16" r="13" fill="none" stroke="rgba(45,77,224,0.15)" strokeWidth="3"/>
              <path d="M16 3a13 13 0 0 1 13 13" fill="none" stroke="#2D4DE0" strokeWidth="3" strokeLinecap="round"/>
            </svg>
          </div>
        ) : showEdit ? (
          <>
            {/* === EDIT FORM (shown only when needed) === */}
            <div style={{ marginBottom: "16px" }}>
              <label style={labelStyle}>Hotel name <span style={{ color: "#DC2626" }}>*</span></label>
              <input type="text" value={hotelName} onChange={(e) => setHotelName(e.target.value)}
                placeholder="e.g. Eastern & Oriental Hotel" style={fieldStyle(hotelName, true)} data-testid="hotel-name-input" />
            </div>
            <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>City</label>
                <input type="text" value={city} onChange={(e) => setCity(e.target.value)}
                  placeholder="e.g. Penang" style={fieldStyle(city, false)} data-testid="city-input" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Country <span style={{ color: "#DC2626" }}>*</span></label>
                <select value={country} onChange={(e) => setCountry(e.target.value)} style={fieldStyle(country, true)} data-testid="country-input">
                  <option value="">Select country</option>
                  {["Albania","Algeria","Argentina","Armenia","Australia","Austria","Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belgium","Bhutan","Bosnia and Herzegovina","Brazil","Brunei","Bulgaria","Cambodia","Cameroon","Canada","Chile","China","Colombia","Costa Rica","Croatia","Cuba","Cyprus","Czech Republic","Denmark","Dominican Republic","Ecuador","Egypt","Estonia","Ethiopia","Fiji","Finland","France","Georgia","Germany","Ghana","Greece","Hong Kong","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Libya","Lithuania","Luxembourg","Macau","Madagascar","Malaysia","Maldives","Malta","Mauritius","Mexico","Mongolia","Morocco","Mozambique","Myanmar","Nepal","Netherlands","New Zealand","Nigeria","North Macedonia","Norway","Oman","Pakistan","Panama","Peru","Philippines","Poland","Portugal","Qatar","Romania","Saudi Arabia","Senegal","Serbia","Seychelles","Singapore","Slovakia","Slovenia","South Africa","South Korea","Spain","Sri Lanka","Sudan","Sweden","Switzerland","Taiwan","Tajikistan","Tanzania","Thailand","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Uganda","United Arab Emirates","United Kingdom","United States","Uruguay","Uzbekistan","Venezuela","Vietnam","Zambia","Zimbabwe","Other"]
                    .map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <button disabled={!canStart} onClick={handleReconfirm} data-testid="confirm-details-btn" style={{
              width: "100%", background: canStart ? "#2D4DE0" : "rgba(45,77,224,0.3)",
              border: "none", borderRadius: "10px", padding: "16px",
              fontFamily: "'Outfit', sans-serif", fontSize: "16px", fontWeight: 700,
              color: "#FFFFFF", cursor: canStart ? "pointer" : "not-allowed",
              boxShadow: canStart ? "0 8px 24px rgba(45,77,224,0.28)" : "none",
            }}>
              Confirm
            </button>
          </>
        ) : (
          <>
            {/* === VERIFIED CARD (default view) === */}
            <div style={{
              background: "#F8F9FF", border: "1px solid rgba(45,77,224,0.15)",
              borderRadius: "14px", padding: "24px", marginBottom: "16px",
            }}>
              <div style={{
                fontFamily: "'Outfit', sans-serif", fontSize: "20px",
                fontWeight: 700, color: "#0A0A1A", marginBottom: "8px",
              }}>
                {hotelName}
              </div>
              <div style={{
                display: "flex", alignItems: "center", gap: "6px",
                fontFamily: "'DM Sans', sans-serif", fontSize: "14px",
                color: "rgba(10,10,26,0.5)",
              }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#2D4DE0" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 1C4.79 1 3 2.79 3 5c0 3 4 8 4 8s4-5 4-8c0-2.21-1.79-4-4-4z"/>
                  <circle cx="7" cy="5" r="1.5"/>
                </svg>
                {locationText}
              </div>
            </div>

            {/* Not right? link */}
            <p style={{
              fontFamily: "'DM Sans', sans-serif", fontSize: "12px",
              color: "rgba(10,10,26,0.4)", textAlign: "center", marginBottom: "24px",
            }}>
              Not right?{" "}
              <span onClick={() => setShowEdit(true)} data-testid="edit-details-btn" style={{
                color: "#2D4DE0", cursor: "pointer", textDecoration: "underline",
              }}>
                Edit details
              </span>
            </p>

            {/* Start Audit button */}
            <button onClick={handleStartAudit} data-testid="start-audit-btn" style={{
              width: "100%", background: "#2D4DE0", border: "none",
              borderRadius: "10px", padding: "16px",
              fontFamily: "'Outfit', sans-serif", fontSize: "16px", fontWeight: 700,
              color: "#FFFFFF", cursor: "pointer",
              boxShadow: "0 8px 24px rgba(45,77,224,0.28)",
            }}>
              Yes, Start My Audit
            </button>
          </>
        )}

      </div>

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default HotelConfirmation;
